import { useState } from 'react';
import { StudentStory } from '../data/storiesData';

interface Props {
  story: StudentStory;
}

function GradeBar({ before, after }: { before: number; after: number }) {
  const [animated, setAnimated] = useState(false);

  return (
    <div
      className="space-y-3"
      ref={(el) => {
        if (el && !animated) {
          const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) setAnimated(true); },
            { threshold: 0.3 }
          );
          observer.observe(el);
        }
      }}
    >
      <div className="flex items-center gap-3">
        <span className="text-xs font-semibold text-gray-400 w-12 shrink-0">Before</span>
        <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-rose-400 rounded-full transition-all duration-1000 ease-out"
            style={{ width: animated ? `${before}%` : '0%' }}
          />
        </div>
        <span className="text-sm font-bold text-rose-500 w-8 text-right">{before}</span>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-xs font-semibold text-gray-400 w-12 shrink-0">After</span>
        <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-emerald-500 rounded-full transition-all duration-1000 ease-out delay-200"
            style={{ width: animated ? `${after}%` : '0%' }}
          />
        </div>
        <span className="text-sm font-bold text-emerald-600 w-8 text-right">{after}</span>
      </div>
    </div>
  );
}

export default function StudentCard({ story }: Props) {
  const [expanded, setExpanded] = useState(false);
  const improvement = story.afterGrade - story.beforeGrade;

  return (
    <div className={`bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden flex flex-col ${story.featured ? 'ring-2 ring-emerald-400' : ''}`}>
      {story.featured && (
        <div className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-xs font-bold px-4 py-1.5 flex items-center gap-1.5">
          <i className="ri-star-fill text-yellow-300"></i>
          Featured Success Story
        </div>
      )}

      <div className="p-6 flex flex-col gap-5 flex-1">
        {/* Header */}
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-emerald-100 shrink-0">
            <img src={story.avatar} alt={story.name} className="w-full h-full object-cover object-top" />
          </div>
          <div>
            <p className="font-bold text-gray-900 text-base">{story.name}</p>
            <p className="text-xs text-gray-500">{story.grade}</p>
            <div className="flex items-center gap-1 mt-1">
              {[1,2,3,4,5].map(s => (
                <i key={s} className="ri-star-fill text-amber-400 text-xs"></i>
              ))}
            </div>
          </div>
          <div className="ml-auto text-center">
            <div className="w-14 h-14 rounded-full bg-emerald-50 border-2 border-emerald-400 flex flex-col items-center justify-center">
              <span className="text-lg font-extrabold text-emerald-600 leading-none">+{improvement}</span>
              <span className="text-[10px] text-emerald-500 font-semibold">pts</span>
            </div>
          </div>
        </div>

        {/* Before / After Grade Labels */}
        <div className="flex items-center gap-3">
          <div className="flex-1 rounded-xl bg-rose-50 border border-rose-100 p-3 text-center">
            <p className="text-xs text-rose-400 font-semibold mb-0.5">Before</p>
            <p className="text-2xl font-extrabold text-rose-500">{story.beforeLabel}</p>
            <p className="text-xs text-rose-400">{story.beforeGrade}%</p>
          </div>
          <div className="w-8 h-8 flex items-center justify-center shrink-0">
            <i className="ri-arrow-right-line text-gray-300 text-xl"></i>
          </div>
          <div className="flex-1 rounded-xl bg-emerald-50 border border-emerald-100 p-3 text-center">
            <p className="text-xs text-emerald-500 font-semibold mb-0.5">After</p>
            <p className="text-2xl font-extrabold text-emerald-600">{story.afterLabel}</p>
            <p className="text-xs text-emerald-500">{story.afterGrade}%</p>
          </div>
        </div>

        {/* Progress Bars */}
        <GradeBar before={story.beforeGrade} after={story.afterGrade} />

        {/* Timeline badge */}
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <i className="ri-time-line text-teal-500"></i>
          <span>Improved in <strong className="text-teal-600">{story.weeksToImprove} weeks</strong></span>
        </div>

        {/* Quote */}
        <blockquote className="text-sm text-gray-600 italic leading-relaxed border-l-4 border-emerald-300 pl-4">
          "{story.quote}"
        </blockquote>

        {/* Parent quote toggle */}
        {story.parentQuote && (
          <div>
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-xs text-emerald-600 font-semibold flex items-center gap-1 cursor-pointer hover:text-emerald-700 transition-colors"
            >
              <i className={`${expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
              {expanded ? 'Hide' : 'Read'} parent review
            </button>
            {expanded && (
              <div className="mt-3 bg-teal-50 rounded-xl p-4">
                <p className="text-sm text-teal-800 italic">"{story.parentQuote}"</p>
                <p className="text-xs text-teal-600 font-semibold mt-2">— {story.parentName}</p>
              </div>
            )}
          </div>
        )}

        {/* Tags */}
        <div className="flex flex-wrap gap-2 mt-auto pt-2">
          {story.tags.map((tag, i) => (
            <span key={i} className="text-xs bg-gray-100 text-gray-500 px-2.5 py-1 rounded-full font-medium">
              {tag}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
